import React, { useEffect } from "react";
import css from "./ComingSoonApp.module.css";
import { Box, Container, Grid, Stack, Typography } from "@mui/material";
import Footer from "../Footer/Footer";
import Navbar from "../../component/navbar/Navbar";
import { useNavigate } from "react-router-dom";
import { FaApple, FaGooglePlay } from "react-icons/fa";
import { IoCheckmarkCircle, IoSpeedometer, IoPhonePortrait, IoCard, IoHeadset, IoPricetag } from "react-icons/io5";
import phone_mockup from "../../assets/coming_soon_app.webp";

const ComingSoonApp = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const appFeatures = [
    { text: "Book services in under 60 seconds", icon: <IoSpeedometer size={20} /> },
    { text: "Real-time pro tracking", icon: <IoPhonePortrait size={20} /> },
    { text: "Secure in-app payments", icon: <IoCard size={20} /> },
    { text: "24/7 customer support", icon: <IoHeadset size={20} /> },
    { text: "Exclusive app-only deals", icon: <IoPricetag size={20} /> },
  ];

  return (
    <div style={{ backgroundColor: "#fff" }}>
      <Navbar />

      {/* Hero Section - Clean White Design */}
      <Box
        sx={{
          backgroundColor: "#fff",
          minHeight: "85vh",
          display: "flex",
          alignItems: "center",
          py: { xs: 6, md: 10 },
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Subtle background pattern */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: "radial-gradient(circle at 1px 1px, #f0f0f0 1px, transparent 0)",
            backgroundSize: "40px 40px",
            opacity: 0.5,
          }}
        />

        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <Grid container spacing={{ xs: 4, md: 8 }} alignItems="center">
            {/* Left Content */}
            <Grid item xs={12} md={6}>
              <Stack spacing={4}>
                <Box>
                  {/* Badge */}
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 1,
                      backgroundColor: "#FFF5F2",
                      border: "1px solid #FFE4DC",
                      borderRadius: "50px",
                      padding: "8px 16px",
                      mb: 3,
                    }}
                  >
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        backgroundColor: "#F15A24",
                        animation: "pulse 2s infinite",
                        "@keyframes pulse": {
                          "0%": { opacity: 1 },
                          "50%": { opacity: 0.5 },
                          "100%": { opacity: 1 },
                        },
                      }}
                    />
                    <Typography
                      sx={{
                        color: "#F15A24",
                        fontSize: "13px",
                        fontWeight: "600",
                      }}
                    >
                      Coming Soon
                    </Typography>
                  </Box>

                  <Typography
                    sx={{
                      color: "#1a1a1a",
                      fontSize: { xs: "32px", sm: "40px", md: "48px" },
                      fontWeight: "700",
                      lineHeight: 1.15,
                      mb: 2,
                    }}
                  >
                    Get Things Done,{" "}
                    <span style={{ color: "#F15A24" }}>Faster</span>
                  </Typography>
                  <Typography
                    sx={{
                      color: "#666",
                      fontSize: { xs: "16px", md: "18px" },
                      lineHeight: 1.7,
                      maxWidth: "480px",
                    }}
                  >
                    Book trusted home service professionals instantly with our mobile app.
                    Your home services, simplified.
                  </Typography>
                </Box>

                {/* Features List */}
                <Stack spacing={2}>
                  {appFeatures.map((feature, index) => (
                    <Stack
                      key={index}
                      direction="row"
                      alignItems="center"
                      spacing={2}
                      sx={{
                        p: 1.5,
                        borderRadius: "10px",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          backgroundColor: "#f9f9f9",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: "10px",
                          backgroundColor: "#FFF5F2",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#F15A24",
                          flexShrink: 0,
                        }}
                      >
                        {feature.icon}
                      </Box>
                      <Typography
                        sx={{
                          color: "#333",
                          fontSize: "15px",
                          fontWeight: "500",
                        }}
                      >
                        {feature.text}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>

                {/* Download Buttons */}
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={2}
                  sx={{ pt: 2 }}
                >
                  {/* App Store Button */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      backgroundColor: "#1a1a1a",
                      borderRadius: "12px",
                      padding: "14px 28px",
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        backgroundColor: "#333",
                        transform: "translateY(-2px)",
                        boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
                      },
                    }}
                  >
                    <FaApple size={28} color="#fff" />
                    <Stack>
                      <Typography
                        sx={{
                          color: "rgba(255,255,255,0.7)",
                          fontSize: "10px",
                          lineHeight: 1,
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }}
                      >
                        Download on the
                      </Typography>
                      <Typography
                        sx={{
                          color: "#fff",
                          fontSize: "17px",
                          fontWeight: "600",
                          lineHeight: 1.3,
                        }}
                      >
                        App Store
                      </Typography>
                    </Stack>
                  </Box>

                  {/* Google Play Button */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      backgroundColor: "#1a1a1a",
                      borderRadius: "12px",
                      padding: "14px 28px",
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        backgroundColor: "#333",
                        transform: "translateY(-2px)",
                        boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
                      },
                    }}
                  >
                    <FaGooglePlay size={24} color="#fff" />
                    <Stack>
                      <Typography
                        sx={{
                          color: "rgba(255,255,255,0.7)",
                          fontSize: "10px",
                          lineHeight: 1,
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }}
                      >
                        Get it on
                      </Typography>
                      <Typography
                        sx={{
                          color: "#fff",
                          fontSize: "17px",
                          fontWeight: "600",
                          lineHeight: 1.3,
                        }}
                      >
                        Google Play
                      </Typography>
                    </Stack>
                  </Box>
                </Stack>

                <Typography
                  sx={{
                    color: "#999",
                    fontSize: "13px",
                  }}
                >
                  Available soon on iOS and Android
                </Typography>
              </Stack>
            </Grid>

            {/* Right Content - Phone Mockup */}
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  position: "relative",
                }}
              >
                {/* Background card */}
                <Box
                  sx={{
                    position: "absolute",
                    width: { xs: "280px", md: "380px" },
                    height: { xs: "400px", md: "500px" },
                    borderRadius: "30px",
                    background: "linear-gradient(145deg, #f8f8f8 0%, #f0f0f0 100%)",
                    transform: "rotate(-3deg)",
                  }}
                />
                <Box
                  sx={{
                    position: "absolute",
                    width: { xs: "280px", md: "380px" },
                    height: { xs: "400px", md: "500px" },
                    borderRadius: "30px",
                    border: "2px solid #F15A24",
                    opacity: 0.2,
                    transform: "rotate(3deg)",
                  }}
                />
                <Box
                  component="img"
                  src={phone_mockup}
                  alt="Done App"
                  sx={{
                    maxWidth: { xs: "260px", md: "320px" },
                    width: "100%",
                    height: "auto",
                    position: "relative",
                    zIndex: 1,
                    filter: "drop-shadow(0 25px 50px rgba(0,0,0,0.15))",
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Stats Section */}
      <Box
        sx={{
          backgroundColor: "#fafafa",
          py: { xs: 6, md: 8 },
          borderTop: "1px solid #eee",
          borderBottom: "1px solid #eee",
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} justifyContent="center">
            {[
              { number: "10K+", label: "Happy Customers" },
              { number: "500+", label: "Verified Pros" },
              { number: "4.8", label: "Average Rating" },
              { number: "24/7", label: "Support" },
            ].map((stat, index) => (
              <Grid item xs={6} sm={3} key={index}>
                <Stack alignItems="center" spacing={0.5}>
                  <Typography
                    sx={{
                      fontSize: { xs: "28px", md: "36px" },
                      fontWeight: "700",
                      color: "#1a1a1a",
                    }}
                  >
                    {stat.number}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      color: "#888",
                      textAlign: "center",
                    }}
                  >
                    {stat.label}
                  </Typography>
                </Stack>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box sx={{ backgroundColor: "#fff", py: { xs: 6, md: 8 } }}>
        <Container maxWidth="sm">
          <Stack alignItems="center" spacing={3} textAlign="center">
            <Typography
              sx={{
                fontSize: { xs: "24px", md: "28px" },
                fontWeight: "600",
                color: "#1a1a1a",
              }}
            >
              Can't wait for the app?
            </Typography>
            <Typography
              sx={{
                fontSize: "16px",
                color: "#666",
                maxWidth: "400px",
              }}
            >
              Book your home services now through our website while we prepare
              something amazing for you.
            </Typography>
            <Box
              onClick={() => navigate("/")}
              sx={{
                backgroundColor: "#F15A24",
                color: "#fff",
                padding: "16px 48px",
                borderRadius: "10px",
                cursor: "pointer",
                fontWeight: "600",
                fontSize: "15px",
                transition: "all 0.3s ease",
                "&:hover": {
                  backgroundColor: "#d94d1a",
                  transform: "translateY(-2px)",
                  boxShadow: "0 8px 25px rgba(241, 90, 36, 0.3)",
                },
              }}
            >
              Book on Website
            </Box>
          </Stack>
        </Container>
      </Box>

      <Footer />
    </div>
  );
};

export default ComingSoonApp;
